import { FieldsContext } from '@/contexts/FieldsContext'
import { formatTurntableDiffRowsFromConcise } from '@/data/data-utils'
import { useDataBufferContext } from '@flatfile/turntable'

import {
  ColumnConfigProps,
  DataChangeEvent,
  OnDataLoadProps,
} from '@flatfile/turntable'
import { useCallback, useContext, useEffect, useRef } from 'react'

export type OnDataLoad<T> = (
  props: OnDataLoadProps,
  columns?: ColumnConfigProps[]
) => Promise<T>

export type OnDataUpdate = (
  changes: DataChangeEvent,
  columnConfig?: ColumnConfigProps[]
) => Promise<DataChangeEvent>

type UseSheetDataProps = {
  sheetId: string
  sheet: any
  workbookId: string
  columnConfig: any
  searchFields: any
  rowCount: number
  lockNumber: number
  getRecordById: (params: {
    sheetId: string
    recordId: string
  }) => Promise<{ dataRowIndex: number; record: any }>
}

const fromMaybe = (value: any, defaultValue: any) => {
  return value ? value : defaultValue
}

export const PAGE_SIZE = 100

export const useSheetViewData = ({
  sheetId,
  sheet,
  workbookId,
  // columnConfig,
  searchFields,
  rowCount,
  lockNumber,
  getRecordById,
}: UseSheetDataProps) => {
  const { columnConfig } = useContext(FieldsContext)

  const isEmptyLoading = useRef(false)

  const { buffer, initLoad, isLoading, isErrored } = useDataBufferContext()

  useEffect(() => {
    buffer.releaseLocks()
  }, [lockNumber, buffer])

  const handleInitLoad = useCallback(async () => {
    await initLoad()
    isEmptyLoading.current = false
  }, [initLoad])

  useEffect(() => {
    buffer._columnConfig = columnConfig
  }, [columnConfig, buffer])

  useEffect(() => {
    // Skip if buffer is not available
    if (!buffer) return

    const bufferId = `${fromMaybe(sheetId, '')}:${JSON.stringify(searchFields)}`
    // Skip re-initialization if bufferId hasn't changed
    if (buffer._bufferId === bufferId) return

    const onLoad = async () => {
      buffer.initialize({
        bufferId,
        params: { searchFields },
        pageSize: PAGE_SIZE,
        columnConfig,
        totalRecords: rowCount,
      })

      if (!buffer.bufferedRecords.length) {
        isEmptyLoading.current = true
      } else {
        buffer.refreshCurrentPages()
      }

      await handleInitLoad()
    }

    onLoad()
  }, [searchFields, columnConfig, sheetId, buffer, rowCount, handleInitLoad])

  useEffect(() => {
    if (buffer) {
      buffer.setTotalRecords(rowCount)
      /* Removes cell edit history from the current buffer because the data shape has changed */
      buffer._cellHistoryCache.delete(buffer._bufferId)
    }
  }, [rowCount, buffer])

  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.data.type === 'updateBufferedRecord') {
        const { dataRowIndex, record } = await getRecordById({
          sheetId,
          recordId: event.data.recordId,
        })
        const turntableRow = formatTurntableDiffRowsFromConcise(
          [{ ...record }],
          [{ ...record, ...event.data.values }],
          0,
          columnConfig
        )[0]
        const indices = Object.keys(event.data.values).map((key) =>
          columnConfig.findIndex((c) => c.value === key)
        )
        const originalRow = buffer.getBufferedRecord(dataRowIndex)
        const updatedRow = originalRow.cells.map((cell, index) => {
          if (indices.includes(index)) {
            return turntableRow.cells[index]
          }
          return cell
        })
        const updatedRowData = { ...originalRow, cells: updatedRow }
        buffer.updateBufferedRecord(dataRowIndex, updatedRowData, true)
      }

      if (event.data.type === 'refetchRecords') {
        buffer.refreshCurrentPages()
        await handleInitLoad()
      }
    },
    [columnConfig]
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])
}
